var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { TabbedForm, useRecordContext, useResourceContext, useTranslate, } from 'react-admin';
import { Alert } from '@mui/material';
import { useResolvedPath, useNavigate } from 'react-router-dom';
import { FormToolbar } from './FormToolbar';
import { useApplyChangesBasedOnSearchParam } from './useApplyChangesBasedOnSearchParam';
import { CreateRevisionOnSave } from '../CreateRevisionOnSave';
import { useGetRevisions } from '../dataProvider';
import { RevisionListWithDetails } from '../revision/RevisionListWithDetails';
/**
 * A replacement for `<TabbedForm>` that adds a new revision after save.
 *
 * It applies the changes from the selected revision when the user reverts
 * to a previous version. It also adds a new tab with the list of revisions.
 * finally, when users click on the Delete button, it deletes all the revisions
 * of the current record.
 *
 * Use it as a child of `<Create>` and `<Edit>`, as you would with `<TabbedForm>`.
 * It expects `<TabbedFormWithRevision.Tab>` components as children.
 *
 * `<TabbedFormWithRevision>` accepts the same props as `<TabbedForm>`, and adds the following:
 *
 * - `allowRevert`: If true, users will be able to revert to a previous version of the record. Defaults to true.
 * - `diff`: The element used to represent the diff between two versions. Defaults to `<DefaultDiffElement />`.
 * - `renderName`: A function to render the author name based on its id
 * - `skipUserDetails`: If true, users won't be prompted to enter a commit message and description when they submit the form. Defaults to false.
 *
 * @see https://marmelab.com/react-admin/TabbedForm.html#props
 * @example
 * import React from 'react';
 * import { Edit, TextInput } from 'react-admin';
 * import { TabbedFormWithRevision } from '@react-admin/ra-history';
 *
 * export const PostEdit = () => (
 *     <Edit>
 *         <TabbedFormWithRevision>
 *             <TabbedFormWithRevision.Tab label="Main">
 *                 <TextInput source="title" />
 *                 <TextInput source="teaser" />
 *                 <TextInput multiline source="body" />
 *             </TabbedFormWithRevision.Tab>
 *         </TabbedFormWithRevision>
 *     </Edit>
 * );
 */
export var TabbedFormWithRevision = function (props) {
    var _a;
    var _b = props.allowRevert, allowRevert = _b === void 0 ? true : _b, children = props.children, diff = props.diff, _c = props.renderName, renderName = _c === void 0 ? function (id) { return id; } : _c, skipUserDetails = props.skipUserDetails, rest = __rest(props, ["allowRevert", "children", "diff", "renderName", "skipUserDetails"]);
    var record = useRecordContext();
    var resource = useResourceContext();
    if (!resource) {
        throw new Error('The TabbedFormWithRevision component must be used inside a ResourceContext.');
    }
    var revisions = useGetRevisions(resource, {
        recordId: (_a = record === null || record === void 0 ? void 0 : record.id) !== null && _a !== void 0 ? _a : 0,
    }, { enabled: !!record }).data;
    var _d = React.useState(false), showAlert = _d[0], setShowAlert = _d[1];
    var resolvedPath = useResolvedPath('');
    var navigate = useNavigate();
    var translate = useTranslate();
    var onRevert = function () {
        if (props.syncWithLocation === false)
            return;
        // the base revert function already uses a Link. We want to override it, so we use a setTimeout
        setTimeout(function () { return navigate(resolvedPath); }, 0);
    };
    return (_jsxs(CreateRevisionOnSave, { skipUserDetails: skipUserDetails, record: props.record, resource: props.resource, children: [showAlert && (_jsx(Alert, { severity: "warning", sx: { width: '100%' }, children: translate('ra-history.info.changes_applied', {
                    _: 'This form has been pre-filled with the changes from a previous revision. You can still You can still modify the data before saving it.',
                }) })), _jsxs(TabbedForm, __assign({ toolbar: _jsx(FormToolbar, {}) }, rest, { children: [children, (revisions === null || revisions === void 0 ? void 0 : revisions.length) > 0 && (_jsx(TabbedForm.Tab, { label: translate('ra-history.revisions.name', {
                            smart_count: revisions === null || revisions === void 0 ? void 0 : revisions.length,
                            _: 'Revisions',
                        }), count: revisions === null || revisions === void 0 ? void 0 : revisions.length, children: _jsx(RevisionListWithDetails, { allowRevert: allowRevert, diff: diff, onRevert: onRevert, record: props.record, renderName: renderName, resource: props.resource }) })), _jsx(ShowUpdatedFieldsAlert, { onChange: setShowAlert })] }))] }));
};
TabbedFormWithRevision.Tab = TabbedForm.Tab;
var ShowUpdatedFieldsAlert = function (_a) {
    var onChange = _a.onChange;
    var showAlert = useApplyChangesBasedOnSearchParam();
    React.useEffect(function () {
        onChange(showAlert);
    }, [showAlert, onChange]);
    return null;
};
